import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  User, Package, CheckCircle2, XCircle, Clock, TrendingUp,
  MapPin, DollarSign, LogIn, LogOut, RefreshCw, Eye,
  Phone, Activity
} from 'lucide-react';
import { api } from '@/lib/api';
import { authService } from '@/services/authService';

interface DelegateStats {
  todayShipments: number;
  pending: number;
  completed: number;
  failed: number;
  totalEarnings: number;
  codCollected: number;
  lastActivity: { action: string; createdAt: string } | null;
  isWorking: boolean;
  checkInTime: string | null;
}

interface Task {
  id: number;
  trackingNumber: string;
  receiverName: string;
  receiverPhone: string;
  receiverAddress: string;
  receiverCity: string;
  receiverArea: string;
  totalAmount: number;
  codAmount: number;
  statusId: number | null;
  statusName: string | null;
  createdAt: string;
}

interface ActivityItem {
  id: number;
  action: string;
  description: string | null;
  location: string | null;
  shipmentTracking: string | null;
  createdAt: string;
}

export default function DelegateDashboardPage() {
  const currentUser = authService.getUser();
  const delegateId = currentUser?.delegateId;
  
  const [stats, setStats] = useState<DelegateStats | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('tasks');
  const [statusFilter, setStatusFilter] = useState('');
  const [showShipmentDetails, setShowShipmentDetails] = useState<Task | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);

  useEffect(() => {
    if (delegateId) {
      loadAll();
    }
  }, []);

  const loadAll = async () => {
    if (!delegateId) return;
    setLoading(true);
    try {
      const [statsRes, tasksRes, activitiesRes] = await Promise.all([
        api.get('/api/delegate-dashboard/' + delegateId + '/stats').catch(() => ({ data: { success: false } })),
        api.get('/api/delegate-dashboard/' + delegateId + '/tasks').catch(() => ({ data: { success: false } })),
        api.get('/api/delegate-dashboard/' + delegateId + '/activities?pageSize=10').catch(() => ({ data: { success: false } }))
      ]);

      if (statsRes?.data?.success) setStats(statsRes.data.data);
      if (tasksRes?.data?.success && Array.isArray(tasksRes.data.data)) setTasks(tasksRes.data.data);
      if (activitiesRes?.data?.success && Array.isArray(activitiesRes.data.data)) setActivities(activitiesRes.data.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    try {
      const res = await api.post('/api/delegate-dashboard/' + delegateId + '/check-in');
      if (res?.data?.success) {
        alert('Check-in successful');
        loadAll();
      } else {
        alert(res?.data?.message || 'Error');
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error');
    }
  };

  const handleCheckOut = async () => {
    if (!confirm('Are you sure you want to check out?')) return;
    try {
      const res = await api.post('/api/delegate-dashboard/' + delegateId + '/check-out');
      if (res?.data?.success) {
        alert('Check-out successful');
        loadAll();
      } else {
        alert(res?.data?.message || 'Error');
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error');
    }
  };

  const updateTaskStatus = async (taskId: number, newStatus: string) => {
    if (!confirm('Update shipment status to ' + newStatus + '?')) return;
    setUpdatingStatus(taskId);
    try {
      const res = await api.put('/api/delegate-dashboard/' + delegateId + '/shipments/' + taskId + '/status', {
        status: newStatus
      });
      if (res?.data?.success) {
        alert('Status updated successfully');
        loadAll();
      } else {
        alert(res?.data?.message || 'Error');
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getStatusBadge = (statusId: number | null, statusName: string | null) => {
    const name = statusName?.toLowerCase() || '';
    if (statusId === 2 || name === 'delivered' || name === 'completed') {
      return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 ml-1" />Delivered</Badge>;
    }
    if (statusId === 3 || name === 'failed' || name === 'cancelled') {
      return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 ml-1" />Failed</Badge>;
    }
    return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 ml-1" />Pending</Badge>;
  };

  const filteredTasks = (tasks || []).filter(t => {
    if (!statusFilter) return true;
    if (statusFilter === 'pending') return t.statusId === 1 || t.statusId === null;
    if (statusFilter === 'completed') return t.statusId === 2;
    if (statusFilter === 'failed') return t.statusId === 3;
    return true;
  });

  const formatCurrency = (value: number) => (Number(value) || 0).toLocaleString('ar-EG', { maximumFractionDigits: 2 }) + ' EGP';

  return (
    <div className="p-6 space-y-6" dir="rtl">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            Delegate Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">Manage daily tasks and update shipment statuses</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadAll} variant="outline" disabled={loading}>
            <RefreshCw className={'w-4 h-4 ml-2 ' + (loading ? 'animate-spin' : '')} />
            Refresh
          </Button>
          {stats?.isWorking ? (
            <Button onClick={handleCheckOut} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 gap-2">
              <LogOut className="w-4 h-4" />
              Check Out
            </Button>
          ) : (
            <Button onClick={handleCheckIn} className="bg-blue-600 hover:bg-blue-700 gap-2">
              <LogIn className="w-4 h-4" />
              Check In
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700">Today Tasks</p>
                <p className="text-2xl font-bold text-blue-900">{stats?.todayShipments || 0}</p>
              </div>
              <Package className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-700">Pending</p>
                <p className="text-2xl font-bold text-yellow-900">{stats?.pending || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700">Delivered</p>
                <p className="text-2xl font-bold text-green-900">{stats?.completed || 0}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-700">Failed</p>
                <p className="text-2xl font-bold text-red-900">{stats?.failed || 0}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-700">Earnings</p>
                <p className="text-xl font-bold text-emerald-900">{formatCurrency(stats?.totalEarnings || 0)}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-700">COD Collected</p>
                <p className="text-xl font-bold text-purple-900">{formatCurrency(stats?.codCollected || 0)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        <div className="flex gap-2 border-b">
          <button onClick={() => setActiveTab('tasks')}
            className={'px-4 py-2 font-medium transition ' + (activeTab === 'tasks' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-900')}>
            Tasks ({filteredTasks.length})
          </button>
          <button onClick={() => setActiveTab('activities')}
            className={'px-4 py-2 font-medium transition ' + (activeTab === 'activities' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-900')}>
            Activity Log
          </button>
        </div>

        {activeTab === 'tasks' && (
          <div className="space-y-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-3">
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-2 border rounded-lg bg-white">
                    <option value="">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Delivered</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredTasks.length === 0 ? (
                <Card className="col-span-full">
                  <CardContent className="text-center py-12">
                    <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No tasks today</p>
                  </CardContent>
                </Card>
              ) : (
                filteredTasks.map((task) => (
                  <Card key={task.id} className="hover:shadow-md transition">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-lg">{task.receiverName}</h3>
                          <p className="text-sm text-gray-500 font-mono">{task.trackingNumber}</p>
                        </div>
                        {getStatusBadge(task.statusId, task.statusName)}
                      </div>
                      
                      <div className="space-y-2 mb-4 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="w-4 h-4" />
                          <span>{task.receiverPhone}</span>
                        </div>
                        <div className="flex items-start gap-2 text-gray-600">
                          <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>{task.receiverAddress}, {task.receiverCity}, {task.receiverArea}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <DollarSign className="w-4 h-4" />
                          <span>{'Total: ' + formatCurrency(task.totalAmount)}</span>
                        </div>
                      </div>

                      <div className="flex gap-2 pt-3 border-t">
                        <Button size="sm" variant="outline" onClick={() => setShowShipmentDetails(task)} className="flex-1">
                          <Eye className="w-3 h-3 ml-1" />Details
                        </Button>
                        {task.statusId !== 2 && task.statusId !== 3 && (
                          <>
                            <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700" 
                              onClick={() => updateTaskStatus(task.id, 'completed')}
                              disabled={updatingStatus === task.id}>
                              {updatingStatus === task.id ? '...' : 'Delivered'}
                            </Button>
                            <Button size="sm" variant="outline" className="flex-1 text-red-600 border-red-200"
                              onClick={() => updateTaskStatus(task.id, 'failed')}
                              disabled={updatingStatus === task.id}>
                              Failed
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'activities' && (
          <Card>
            <CardContent className="p-0">
              {activities.length === 0 ? (
                <div className="text-center py-12">
                  <Activity className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No activity recorded</p>
                </div>
              ) : (
                <div className="divide-y">
                  {activities.map((activity) => (
                    <div key={activity.id} className="p-4 flex items-start gap-3 hover:bg-gray-50">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Activity className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-semibold">{activity.action}</p>
                          <span className="text-xs text-gray-500">
                            {activity.createdAt ? new Date(activity.createdAt).toLocaleString('ar-EG') : ''}
                          </span>
                        </div>
                        {activity.description && <p className="text-sm text-gray-600">{activity.description}</p>}
                        {activity.shipmentTracking && (
                          <Badge variant="outline" className="mt-1">{'Shipment: ' + activity.shipmentTracking}</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {showShipmentDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-500" />
                Shipment Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Tracking Number</p>
                <p className="font-mono font-bold">{showShipmentDetails.trackingNumber}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Receiver</p>
                <p className="font-semibold">{showShipmentDetails.receiverName}</p>
                <p className="text-sm text-gray-600 flex items-center gap-1">
                  <Phone className="w-3 h-3" />{showShipmentDetails.receiverPhone}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Delivery Address</p>
                <p className="text-sm">{showShipmentDetails.receiverAddress}</p>
                <p className="text-sm text-gray-600">{showShipmentDetails.receiverCity + ', ' + showShipmentDetails.receiverArea}</p>
              </div>
              <div className="flex justify-between pt-3 border-t">
                <div>
                  <p className="text-xs text-gray-500">Total Amount</p>
                  <p className="font-bold text-blue-600">{formatCurrency(showShipmentDetails.totalAmount)}</p>
                </div>
                {showShipmentDetails.codAmount > 0 && (
                  <div className="text-left">
                    <p className="text-xs text-gray-500">COD Amount</p>
                    <p className="font-bold text-green-600">{formatCurrency(showShipmentDetails.codAmount)}</p>
                  </div>
                )}
              </div>
              <Button onClick={() => setShowShipmentDetails(null)} className="w-full mt-2">Close</Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
